/* MIT License

Copyright (c) 2019 Benoit Baudaux

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import React from 'react';
import * as Redux from 'redux'
import { connect } from 'react-redux'

import mapboxgl from 'mapbox-gl';
import './mapbox-gl.css'
import './Map.css'

import { Poi, PoiActionTypes, Photo } from './PoiReducerTypes';
import { addPoi, selectPoi, movePoi } from './PoiReducerActions';
import { styleLoaded } from './StyleReducerActions';
import { RootState } from './RootReducer';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkedAlt, faRoute, faCamera } from '@fortawesome/free-solid-svg-icons'

import EXIF from 'exif-js';

declare var gpxParser: any;

interface IProps {
}

interface StateProps {
    poiList: Poi[];
    styleUrl: string;
    selectedMarker: string;
    selectedPoi: number;
    panelSize: [number,number];
}
       
interface DispatchProps {
    addPoi: (newPoi: Poi) => void;
    selectPoi: (ref: number) => void;
    styleLoaded: (styleName: string, sprite: any, imageUrl: string) => void;
    movePoi: (ref: number, points: Array<mapboxgl.LngLat>) => void;
}

interface IState {

    editMode: boolean;
    editPhotoUrl: boolean;
    photoUrl: string;
    selectedPhotoUrl: string;
}

type Props = StateProps & DispatchProps & IProps;

function mapStateToProps(state: RootState, ownProps: IProps): StateProps {

    return { 
        poiList: state.poi.poiList,
        selectedPoi: state.poi.selectedPoi,
        styleUrl: state.style.styleUrl,
        selectedMarker: state.style.selectedMarker,
        panelSize: state.style.panelSize,
    };
}

const mapDispatchToProps = {
    
    addPoi,
    selectPoi,
    styleLoaded,
    movePoi,
};

class Map extends React.Component <Props,IState> {

    private gpxInputRef = React.createRef<HTMLInputElement>();
    private imgRef = React.createRef<HTMLImageElement>();

    private map: mapboxgl.Map | null = null;

    private currentStyleUrl: string;

    private popup: mapboxgl.Popup;

    private focusedPoi: number = -1;

    private movingFeature: any = null;
    private geojsondata: any;

    constructor(props: Props) {
        
        super(props);

        this.currentStyleUrl = '';

        this.state = {
            editMode: false,
            editPhotoUrl: false,
            photoUrl: "",
            selectedPhotoUrl: ""
        };

        this.popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false});

        this.toggleEdit = this.toggleEdit.bind(this);
        this.openPathDialog = this.openPathDialog.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.openPhotoDialog = this.openPhotoDialog.bind(this);
        this.onFileSelected = this.onFileSelected.bind(this);
        this.onMouseEnterMarkers = this.onMouseEnterMarkers.bind(this);
        this.onMouseEnterPaths = this.onMouseEnterPaths.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
    }

    addPoi(e: mapboxgl.MapMouseEvent) {

        if (e != null) {

            const metadata = [ {lang:"fr", title:"Sans titre", desc:"", link: "", linkLabel: "",} ];

            const poi: Poi = {
                ref: Date.now(),
                metadata: metadata,
                photos: [],
                points: [e.lngLat],
                symbol: this.props.selectedMarker,
                symbolSize: 0.5,
            };

            this.props.addPoi(poi);
        }
    }

    updateMarkers() {

        if (this.map) {

            const source: mapboxgl.GeoJSONSource = this.map.getSource('markers') as mapboxgl.GeoJSONSource;

            if (source) {

                this.geojsondata = {
                    type: 'FeatureCollection',
                    features: new Array()
                };

                for (let poi of this.props.poiList) {

                    let feature: any;

                    if (poi.points.length == 1) {

                        feature = {

                            'type': 'Feature',
                            'geometry': {
                                'type': 'Point',
                                'coordinates': [poi.points[0].lng,poi.points[0].lat]
                            },
                            'properties': {
                                'icon': poi.symbol,
                                'icon-size': (poi.ref == this.props.selectedPoi)?2*poi.symbolSize:poi.symbolSize,
                                'ref': poi.ref,
                                'metadata': poi.metadata,
                            }
                        };
                    }
                    else if (poi.points.length > 1) {

                        feature = {

                            'type': 'Feature',
                            'geometry': {
                                'type': 'LineString',
                                'coordinates': poi.points.map(point => [point.lng,point.lat])
                            },
                            'properties': {
                                'color': poi.symbol,
                                'line-width': poi.symbolSize,
                                'ref': poi.ref,
                                'metadata': poi.metadata,
                            }
                        };
                    }
                    
                    this.geojsondata.features.push(feature);

                    if ( (poi.ref == this.props.selectedPoi) && (this.focusedPoi != this.props.selectedPoi) ) {

                        this.focusedPoi = this.props.selectedPoi;

                        this.map.flyTo({center: poi.points[0], zoom: 15});
                    }
                }
                
                source.setData(this.geojsondata);
            }
        }
    }

    selectPoi(ref: number) {

        this.focusedPoi = ref;
        this.props.selectPoi(ref);
    }

    toggleEdit() {

        this.setState({editMode: !this.state.editMode});
    }

    exitEdit() {

        this.setState({editMode: false});
    }

    onFileSelected(e: any) {

        let file = e.target.files[0];

        if (!file) {

          return;
        }

        const _this = this;

        if (e.target.files[0].name.indexOf(".gpx") > 0) {

          var reader = new FileReader();

          reader.onload = function(event: any) {

            if (event && event.target) {

                let gpx = new gpxParser(); //Create gpxParser Object

                gpx.parse(event.target.result);

                console.log(gpx);

                let i = 0;

                for (let track of gpx.tracks) {

                    const title: string = track.name as string | "Sans titre";

                    const metadata = [ {lang:"fr", title: title, desc:"", link: "", linkLabel: "",} ];

                    const poi: Poi = {
                        ref: Date.now()+i,
                        metadata: metadata,
                        photos: [],
                        points: track.points.map((point:any) => new mapboxgl.LngLat(point.lon,point.lat)),
                        symbol: '#ff0000',
                        symbolSize: 2
                    };

                    _this.props.addPoi(poi);

                    i += 1;
                }
            }
          };

          reader.readAsText(file);
        }
    }

    openPathDialog() {

        if (this.gpxInputRef && this.gpxInputRef.current) {
          this.gpxInputRef.current.click();

          this.gpxInputRef.current.addEventListener('change', this.onFileSelected, false);
        }
    }

    openPhotoDialog() {

        //this.setState({photoUrl: "https://maps.meandmaps.com/randocauterets/pontdespagne.jpeg"});
        this.setState({selectedPhotoUrl: "", photoUrl: "", editPhotoUrl: true});
    }

    onMouseEnterMarkers(e: any) {

        if (!this.movingFeature) {
            e.target.getCanvas().style.cursor = 'pointer';
        }
        else {

            return;
        }

        if (this.state.editMode) {

            this.popup
                .setLngLat(e.lngLat)
                .setHTML('<span>Click on the symbol for moving it.</span><br/><span>And click again for setting the new location.</span>')
                .addTo(this.map!);
        }
        else {

            // I don not know why it is a string, then we have to parse it
            let metadata = JSON.parse(e.features[0].properties.metadata);

            this.popup
                .setLngLat(e.lngLat)
                .setHTML('<span>'+metadata[0].title+'</span>')
                .addTo(this.map!);
        }
    }

    onMouseEnterPaths(e: any) {

        e.target.getCanvas().style.cursor = 'pointer';
        // I don not know why it is a string, then we have to parse it

        let metadata = JSON.parse(e.features[0].properties.metadata);

        this.popup
            .setLngLat(e.lngLat)
            .setHTML('<span>'+metadata[0].title+'</span>')
            .addTo(this.map!);
    }

    onMouseLeave(e: mapboxgl.MapMouseEvent) {

        if (!this.movingFeature) {

            if (this.state.editMode) {
                e.target.getCanvas().style.cursor = 'crosshair';
            }
            else {
                e.target.getCanvas().style.cursor = '';
            }
        }

        this.popup.remove();
    }

    loadMap() {

        if (this.currentStyleUrl === this.props.styleUrl)
            return;

        this.currentStyleUrl = this.props.styleUrl;

        if (this.map) {

            this.map.remove();

            this.map = null;
        }

        if (this.props.styleUrl.length > 0) {

            let index = this.props.styleUrl.indexOf("access_token");

            let styleUrl: string = this.props.styleUrl;

            if ( index >= 0) {

                mapboxgl.accessToken = this.props.styleUrl.substring(index+13);

                console.log(mapboxgl.accessToken);

                //styleUrl = styleUrl.substring(0,index);
            }
            else {

                mapboxgl.accessToken = "";
            }

            this.map = new mapboxgl.Map({
                container: 'map',
                style: this.props.styleUrl,
                attributionControl: false
            });

            this.map.addControl(new mapboxgl.NavigationControl({

                visualizePitch: true
            }), 'bottom-right');
    
            if (this.map) {
    
                this.map.on('load', () => {
    
                    if (this.map) {
    
                        const mapName: string = this.map.getStyle().name || '';

                        let spriteJsonUrl: string = this.map.getStyle().sprite + "@2x.json" || '';
                        let spritePngUrl: string = this.map.getStyle().sprite + "@2x.png" || '';

                        fetch(spriteJsonUrl)
                            .then(res => res.json())
                            .then(
                                (result) => {

                                    this.props.styleLoaded(mapName,result,spritePngUrl);
                                },
                                // Note: it's important to handle errors here
                                // instead of a catch() block so that we don't swallow
                                // exceptions from actual bugs in components.
                                (error) => {
                                    
                                    spriteJsonUrl = this.map!.getStyle().sprite + ".json" || '';
                                    spritePngUrl = this.map!.getStyle().sprite + ".png" || '';

                                    fetch(spriteJsonUrl)
                                        .then(res => res.json())
                                        .then(
                                            (result) => {

                                                this.props.styleLoaded(mapName,result,spritePngUrl);
                                            },
                                            // Note: it's important to handle errors here
                                            // instead of a catch() block so that we don't swallow
                                            // exceptions from actual bugs in components.
                                            (error) => {
                                                
                                                console.log(error);
                                            }
                                        );
                                }
                            );

                        this.map.addSource('markers', { type: 'geojson', data: {
                                type: 'FeatureCollection',
                                features: []
                            }
                        });

                        this.map.addLayer({
                            'id': 'markers',
                            'type': 'symbol',
                            'source': 'markers',
                            'filter': ['==', '$type', 'Point'],
                            'layout': {
                                'icon-image': ['get', 'icon'],
                                "icon-size": ['get', 'icon-size'],
                                "icon-allow-overlap": true
                            }
                        });

                        this.map.addLayer({
                            'id': 'paths',
                            'type': 'line',
                            'source': 'markers',
                            'filter': ['==', '$type', 'LineString'],
                            'paint': {
                                'line-color': ['get', 'color'],
                                'line-width': ['get', 'line-width']
                              }
                        });
                    }
                    
                });

                this.map.on('mousedown', (e: mapboxgl.MapMouseEvent) => {

                    if (this.map && (!this.movingFeature) && this.state.editMode) {

                        let features = this.map.queryRenderedFeatures(e.point, {layers: ['markers']});

                        if (!features || features.length == 0) {
                        
                            this.addPoi(e);

                            this.exitEdit();
                        }
                    }
                });

                this.map.on('mousemove', (e: mapboxgl.MapMouseEvent) => {

                    if (this.movingFeature && this.map) {

                        let feature = this.geojsondata.features.find((feature:any) => (feature && feature.properties.ref == this.movingFeature.properties.ref))

                        if (feature) {

                            feature.geometry.coordinates = [e.lngLat.lng,e.lngLat.lat];

                            const source: mapboxgl.GeoJSONSource = this.map.getSource('markers') as mapboxgl.GeoJSONSource;

                            if (source) {

                                source.setData(this.geojsondata);
                            }
                        }
                    }
                });

                this.map.on('click', 'markers', (e: any) => {

                    if (e) {

                        if (this.state.editMode) {

                            if (this.movingFeature) {

                                this.props.movePoi(this.movingFeature.properties.ref, [e.lngLat]);

                                this.movingFeature = null;
                                e.target.getCanvas().style.cursor = 'crosshair';

                                this.exitEdit();
                            }
                            else {

                                this.movingFeature = e.features[0];
                                e.target.getCanvas().style.cursor = 'move';
                                this.popup.remove();
                            }
                        }
                        else {

                            this.selectPoi(e.features[0].properties.ref);
                        }
                        
                        //console.log('click on POI '+e.features[0].properties.ref);
                    }
                });

                this.map.on('click', 'paths', (e: any) => {

                    if (e) {

                        if (!this.state.editMode) {
                            this.selectPoi(e.features[0].properties.ref);
                        }
                    }
                });

                // Change the cursor to a pointer when the mouse is over the places layer.
                this.map.on('mouseenter', 'markers', this.onMouseEnterMarkers);
                this.map.on('mousemove', 'markers', this.onMouseEnterMarkers);
                this.map.on('mouseenter', 'paths', this.onMouseEnterPaths);
                this.map.on('mousemove', 'paths', this.onMouseEnterPaths);
                
                // Change it back to a pointer when it leaves.
                this.map.on('mouseleave', 'markers', this.onMouseLeave);
                this.map.on('mouseleave', 'paths', this.onMouseLeave);
            }
        }
    }

    componentDidMount() {

        this.loadMap();

        this.updateMarkers();
    }

    componentDidUpdate() {

        this.loadMap();

        this.updateMarkers();

        if (this.map) {

            this.map.resize();

            if (this.state.editMode) {

                this.map.getCanvas().style.cursor = 'crosshair';
            }
            else {

                this.map.getCanvas().style.cursor = '';
            }
        }
    }

    convCoords(coords: any) {

        const deg = coords[0].numerator/coords[0].denominator;
        const min = coords[1].numerator/coords[1].denominator;
        const sec = coords[2].numerator/coords[2].denominator;

        return deg + (min/60) + (sec/3600);
    }

    onKeyUp(e: any) {

        if (e.keyCode === 13) {
          
          e.preventDefault();

          this.setState({selectedPhotoUrl: this.state.photoUrl});
        }
    }

    onChange(e: any) {

        this.setState({photoUrl: e.target.value});
    }

    onBlur(e: any) {

        this.setState({editPhotoUrl: false});
    }

    renderPhoto() {

        if (this.state.selectedPhotoUrl.length > 0) {

            return (<img id="photo" ref={this.imgRef} src={this.state.selectedPhotoUrl} onLoad={(function (_this) {

                        return function (event: any) {

                            EXIF.getData(_this.imgRef.current, function() {

                                const tags = EXIF.getAllTags(_this.imgRef.current);

                                if (tags && ("GPSLatitudeRef" in tags)) {

                                    const lat = ((tags.GPSLatitudeRef == "N")?1:-1) * _this.convCoords(tags.GPSLatitude);
                                    const lon = ((tags.GPSLongitudeRef == "E")?1:-1) * _this.convCoords(tags.GPSLongitude);

                                    const metadata = [ {lang:"fr", title: "Sans titre", desc:"", link: "", linkLabel: "",} ];

                                    const photo: Photo = {

                                        url: _this.imgRef.current!.src,
                                        width: _this.imgRef.current!.naturalWidth,
                                        height: _this.imgRef.current!.naturalHeight
                                    };

                                    const poi: Poi = {
                                        ref: Date.now(),
                                        metadata: metadata,
                                        photos: [ photo ],
                                        points: [ new mapboxgl.LngLat(lon,lat) ],
                                        symbol: _this.props.selectedMarker,
                                        symbolSize: 0.5
                                    };

                                    _this.props.addPoi(poi);
                                }
                            });
                        }

                    }) (this)}></img>);
        }
        else if (this.state.editPhotoUrl) {

            return (<div id="photoUrl"><input type="text" placeholder="Type photo url and press the <Enter> key" value={this.state.photoUrl} onChange={this.onChange} onClick={e => {e.stopPropagation()}} onKeyUp={this.onKeyUp} onBlur={this.onBlur}></input></div>)
        }
    }
        
    render() {
        return (
            <div className="Map"><div id="map"></div><FontAwesomeIcon id="edit" className={(this.state.editMode == true) ? 'editOn' : 'editOff'} icon={faMapMarkedAlt} onClick={this.toggleEdit} title="Activate/deactivate POI edit mode"/><FontAwesomeIcon id="addPath" className="editOff" icon={faRoute} onClick={this.openPathDialog} title="Add GPX path"/><input ref={this.gpxInputRef} id="gpxFileInput" accept=".gpx" type="file" name="name"/><FontAwesomeIcon id="addPhoto" className="editOff" icon={faCamera} onClick={this.openPhotoDialog} title="Add geolocated photo"/>{this.renderPhoto()}</div>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Map)